import { enableBodyScroll, disableBodyScroll } from 'body-scroll-lock';
import anime from 'animejs';
import { duration, queryFocusable } from './utilities';

const headers = document.querySelectorAll<HTMLElement>('[data-header]');

headers.forEach((header) => {
	const button = header.querySelector<HTMLElement>('[data-button]');
	const menu = header.querySelector<HTMLElement>('[data-menu]');
	const logoWhite = header.querySelector<HTMLElement>('[data-logo-white]');

	if (!button || !menu) return;

	const openLabel = button.getAttribute('aria-label');
	const closeLabel = button.getAttribute('data-button-close-label');
	const items = menu.querySelectorAll<HTMLElement>('li');
	const logos = menu.querySelector<HTMLElement>('[data-logos]');
	const cover = header.querySelector<HTMLElement>('[data-header-cover]');

	let neverOpened = true;
	let menuOpen = false;
	const menuDisplayMode = 'flex'; // window.getComputedStyle(menu).display;

	/**
	 * Animations
	 */

	const openSymbol = button.querySelector('[data-open-symbol]');
	const closeSymbol = button.querySelector('[data-close-symbol]');
	const menuButtonAnimation = anime.timeline({
		direction: 'alternate',
		autoplay: false,
		loop: false,
		duration: duration(450),
		easing: 'cubicBezier(0.32, -0.24, 0, 1.4)',
	});
	menuButtonAnimation.add(
		{
			targets: openSymbol,
			rotate: [0, -45],
			scaleY: [1, 0.1],
			scaleX: [1, 0.8],
			opacity: [1, 0],
		},
		0,
	);
	menuButtonAnimation.add(
		{
			targets: closeSymbol,
			rotate: [45, 0],
			opacity: [0, 1],
			scale: [0.8, 1],
		},
		0,
	);

	const animation = anime.timeline({
		direction: 'alternate',
		autoplay: false,
		loop: false,
		begin() {},
		complete() {
			if (animation.progress === 0) {
				menu.style.display = 'none';
				if (cover) cover.style.display = 'none';
			}
		},
	});

	// The menu
	animation.add(
		{
			targets: menu,
			translateX: ['-100%', '0%'],
			// opacity: [0, 1],
			easing: 'cubicBezier(.16,.13,.12,1)',
			duration: duration(400),
		},
		0,
	);

	// Background cover
	animation.add(
		{
			targets: cover,
			opacity: [0, 0.4],
			easing: 'linear',
			duration: duration(400),
		},
		0,
	);

	// White mask of logo
	animation.add(
		{
			targets: logoWhite,
			scaleX: [0, 1],
			easing: 'linear',
			duration: duration(60),
		},
		duration(10),
	);

	// Each menu item
	animation.add(
		{
			targets: items,
			opacity: [0, 1],
			translateX: ['-2.5rem', '0rem'],
			easing: 'cubicBezier(0.1, 1.54, 0.62, 0.97)',
			duration: duration(300),
			delay: (_, i) => duration(i * 50),
		},
		duration(50),
	);

	// Newsletter box
	animation.add(
		{
			targets: logos,
			opacity: [0, 1],
			translateY: ['2rem', '0rem'],
			easing: 'cubicBezier(0.1, 1.54, 0.62, 0.97)',
			duration: duration(300),
		},
		duration(items.length * 50),
	);

	/**
	 * Open / close functions
	 */

	const openMenu = () => {
		menuOpen = true;

		if (neverOpened) {
			menu.style.display = menuDisplayMode;
			menu.style.transform = 'translateX(-100%)';
			neverOpened = false;
		}

		if (animation.progress === 0) {
			menu.style.display = menuDisplayMode;
			if (cover) cover.style.display = 'block';
		}

		if (closeLabel) {
			button.setAttribute('aria-label', closeLabel);
		}
		disableBodyScroll(menu);

		if (menuButtonAnimation.reversed) {
			menuButtonAnimation.reverse();
		}
		menuButtonAnimation.play();

		if (animation.reversed) {
			animation.reverse();
		}

		animation.play();
	};

	const closeMenu = () => {
		menuOpen = false;

		if (openLabel) {
			button.setAttribute('aria-label', openLabel);
		}
		enableBodyScroll(menu);

		if (!menuButtonAnimation.reversed) {
			menuButtonAnimation.reverse();
		}
		menuButtonAnimation.play();

		if (!animation.reversed) {
			animation.reverse();
		}
		animation.play();
	};

	/**
	 * Event handlers
	 */

	// Open when clicking button
	button.addEventListener('click', () => {
		if (!menuOpen) {
			openMenu();
			queryFocusable(menu)?.focus();
		} else {
			closeMenu();
		}
	});

	// Close when clicking outside element
	document.addEventListener('click', (ev) => {
		if (
			menuOpen &&
			ev.target instanceof HTMLElement &&
			(!header.contains(ev.target) || ev.target === cover)
		) {
			closeMenu();
		}
	});

	// Close when pressing Escape
	document.addEventListener('keyup', (ev) => {
		if (ev.key === 'Escape' && menuOpen) {
			button.focus();
			closeMenu();
		}
	});

	// Trap focus
	document.addEventListener('keyup', (ev) => {
		if (
			menuOpen &&
			ev.key === 'Tab' &&
			ev.target instanceof HTMLElement &&
			!header.contains(ev.target)
		) {
			if (ev.shiftKey) {
				queryFocusable(header, true)?.focus();
			} else {
				queryFocusable(header)?.focus();
			}
		}
	});
});
